/**
 * Cell renderer utility for the DataList component.
 *
 * Provides formatting functions for each column type (text, number, currency,
 * date, badge, boolean, link, icon, custom) and a unified `renderCellValue`
 * function that returns a `CellRenderResult` describing how the cell should
 * be rendered by the component layer.
 */

import type {
  ColumnConfig,
  NumberColumnOptions,
  CurrencyColumnOptions,
  DateColumnOptions,
  TextColumnOptions,
  BadgeColumnOptions,
  BooleanColumnOptions,
  LinkColumnOptions,
  IconColumnOptions,
  CustomColumnOptions,
} from '../types';

import { DEFAULT_BOOLEAN_ICONS, DEFAULT_BOOLEAN_COLORS } from '../constants';

// ---------------------------------------------------------------------------
// Safe string coercion
// ---------------------------------------------------------------------------

/**
 * Safely converts an `unknown` value to a string without risking
 * `[object Object]` output.
 *
 * - `null` / `undefined` → `''`
 * - Primitives (string, number, boolean, bigint) → `String(value)`
 * - Everything else (objects, arrays) → `JSON.stringify(value)`
 *
 * Exported so other DataList modules can reuse the same logic.
 */
export function safeString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return JSON.stringify(value);
}

// ---------------------------------------------------------------------------
// CellRenderResult
// ---------------------------------------------------------------------------

/**
 * Describes how a cell value should be rendered.
 *
 * For simple types (text, number, currency, date) the `text` field contains
 * the formatted display string. For complex types (badge, boolean, link, icon,
 * custom) additional metadata fields are populated so that the component layer
 * can render the appropriate React component.
 */
export interface CellRenderResult {
  /** The type of rendering needed */
  type: 'text' | 'badge' | 'boolean' | 'link' | 'icon' | 'custom';
  /** The display text (for text, number, currency, date types) */
  text: string;
  /** Badge variant (for badge type) */
  badgeVariant?: string;
  /** Icon name (for boolean and icon types) */
  icon?: string;
  /** Icon color (for boolean type) */
  iconColor?: string;
  /** Link href (for link type) */
  href?: string;
  /** Whether link opens in new tab */
  external?: boolean;
  /** Custom renderer key (for custom type) */
  rendererKey?: string;
}

// ---------------------------------------------------------------------------
// formatNumber
// ---------------------------------------------------------------------------

/**
 * Formats a number value with optional prefix/suffix.
 * Uses `Intl.NumberFormat` when a format string is provided.
 *
 * Supported format patterns:
 * - `"0,0"` -- grouped integers (e.g. 1,234)
 * - `"0,0.00"` -- grouped with 2 decimal places (e.g. 1,234.56)
 * - `"0.00"` -- no grouping, 2 decimal places (e.g. 1234.56)
 * - Any string containing digits after a dot determines decimal places
 *
 * When no format is provided the number is converted with `String()`.
 */
export function formatNumber(
  value: number,
  options?: NumberColumnOptions,
): string {
  const prefix = options?.prefix ?? '';
  const suffix = options?.suffix ?? '';
  const format = options?.format;

  let formatted: string;

  if (format) {
    const useGrouping = format.includes(',');
    // Count digits after the decimal point in the format string
    const dotIndex = format.lastIndexOf('.');
    const minimumFractionDigits =
      dotIndex >= 0 ? format.length - dotIndex - 1 : 0;

    formatted = new Intl.NumberFormat('en-US', {
      useGrouping,
      minimumFractionDigits,
      maximumFractionDigits: minimumFractionDigits,
    }).format(value);
  } else {
    formatted = String(value);
  }

  return `${prefix}${formatted}${suffix}`;
}

// ---------------------------------------------------------------------------
// formatCurrency
// ---------------------------------------------------------------------------

/**
 * Formats a currency value using `Intl.NumberFormat`.
 * Defaults to EUR currency and fr-FR locale if not specified.
 */
export function formatCurrency(
  value: number,
  options?: CurrencyColumnOptions,
): string {
  const currency = options?.currency ?? 'EUR';
  const locale = options?.locale ?? 'fr-FR';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

// ---------------------------------------------------------------------------
// formatDate
// ---------------------------------------------------------------------------

/**
 * Formats a date value.
 * Defaults to `'dd/MM/yyyy'` if no format is specified.
 *
 * Handles `Date` objects and ISO date strings. Returns an empty string for
 * invalid dates.
 *
 * Supported format tokens (subset matching date-fns conventions):
 * - `dd`   -- zero-padded day of month (01-31)
 * - `d`    -- day of month (1-31)
 * - `MM`   -- zero-padded month (01-12)
 * - `M`    -- month (1-12)
 * - `yyyy` -- four-digit year
 * - `yy`   -- two-digit year
 * - `HH`   -- zero-padded 24h hours (00-23)
 * - `mm`   -- zero-padded minutes (00-59)
 * - `ss`   -- zero-padded seconds (00-59)
 */
export function formatDate(
  value: string | Date,
  options?: DateColumnOptions,
): string {
  const format = options?.format ?? 'dd/MM/yyyy';

  let date: Date;

  if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const pad = (n: number): string => n.toString().padStart(2, '0');

  // Replace tokens from longest to shortest to avoid partial replacement
  let result = format;
  result = result.replaceAll('yyyy', year.toString());
  result = result.replaceAll('yy', (year % 100).toString().padStart(2, '0'));
  result = result.replaceAll('dd', pad(day));
  result = result.replaceAll(
    /\bd\b/g,
    day.toString(),
  );
  result = result.replaceAll('MM', pad(month));
  result = result.replaceAll(
    /\bM\b/g,
    month.toString(),
  );
  result = result.replaceAll('HH', pad(hours));
  result = result.replaceAll('mm', pad(minutes));
  result = result.replaceAll('ss', pad(seconds));

  return result;
}

// ---------------------------------------------------------------------------
// truncateText
// ---------------------------------------------------------------------------

/**
 * Truncates text to `maxLength` characters, appending an ellipsis if
 * the text was shortened.
 */
export function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength)}...`;
}

// ---------------------------------------------------------------------------
// renderCellValue
// ---------------------------------------------------------------------------

/**
 * Renders a cell value based on column type.
 *
 * For simple types (text, number, currency, date) returns a
 * `CellRenderResult` with `type: 'text'` and the formatted string.
 *
 * For complex types (badge, boolean, link, icon, custom) returns metadata
 * that the component layer uses to render with React components.
 */
export function renderCellValue(
  value: unknown,
  column: ColumnConfig,
  row: Record<string, unknown>,
): CellRenderResult {
  const columnType = column.type ?? 'text';
  const typeOptions = column.typeOptions;

  switch (columnType) {
    case 'text':
      return renderText(value, typeOptions as TextColumnOptions | undefined);

    case 'number':
      return renderNumber(
        value,
        typeOptions as NumberColumnOptions | undefined,
      );

    case 'currency':
      return renderCurrency(
        value,
        typeOptions as CurrencyColumnOptions | undefined,
      );

    case 'date':
      return renderDateCell(
        value,
        typeOptions as DateColumnOptions | undefined,
      );

    case 'badge':
      return renderBadge(
        value,
        typeOptions as BadgeColumnOptions | undefined,
      );

    case 'boolean':
      return renderBoolean(
        value,
        typeOptions as BooleanColumnOptions | undefined,
      );

    case 'link':
      return renderLink(
        value,
        typeOptions as LinkColumnOptions | undefined,
        row,
      );

    case 'icon':
      return renderIcon(value, typeOptions as IconColumnOptions | undefined);

    case 'custom':
      return renderCustom(
        value,
        typeOptions as CustomColumnOptions | undefined,
      );

    default:
      return { type: 'text', text: safeString(value) };
  }
}

// ---------------------------------------------------------------------------
// Internal renderers
// ---------------------------------------------------------------------------

function renderText(
  value: unknown,
  options?: TextColumnOptions,
): CellRenderResult {
  const text = safeString(value);

  if (options?.truncate && text.length > 0) {
    return { type: 'text', text: truncateText(text, options.truncate) };
  }

  return { type: 'text', text };
}

function renderNumber(
  value: unknown,
  options?: NumberColumnOptions,
): CellRenderResult {
  if (value == null || value === '') {
    return { type: 'text', text: '' };
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return { type: 'text', text: '' };
  }

  return { type: 'text', text: formatNumber(num, options) };
}

function renderCurrency(
  value: unknown,
  options?: CurrencyColumnOptions,
): CellRenderResult {
  if (value == null || value === '') {
    return { type: 'text', text: '' };
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return { type: 'text', text: '' };
  }

  return { type: 'text', text: formatCurrency(num, options) };
}

function renderDateCell(
  value: unknown,
  options?: DateColumnOptions,
): CellRenderResult {
  if (value == null || value === '') {
    return { type: 'text', text: '' };
  }

  const text = formatDate(value as string | Date, options);
  return { type: 'text', text };
}

function renderBadge(
  value: unknown,
  options?: BadgeColumnOptions,
): CellRenderResult {
  const text = safeString(value);
  const variantMap = options?.variantMap ?? {};
  const badgeVariant = variantMap[text] ?? 'default';

  return { type: 'badge', text, badgeVariant };
}

function renderBoolean(
  value: unknown,
  options?: BooleanColumnOptions,
): CellRenderResult {
  const isTruthy = Boolean(value);

  const icon = isTruthy
    ? (options?.trueIcon ?? DEFAULT_BOOLEAN_ICONS.true)
    : (options?.falseIcon ?? DEFAULT_BOOLEAN_ICONS.false);

  const iconColor = isTruthy
    ? (options?.trueColor ?? DEFAULT_BOOLEAN_COLORS.true)
    : (options?.falseColor ?? DEFAULT_BOOLEAN_COLORS.false);

  return {
    type: 'boolean',
    text: '',
    icon,
    iconColor,
  };
}

function renderLink(
  value: unknown,
  options?: LinkColumnOptions,
  row?: Record<string, unknown>,
): CellRenderResult {
  const text = safeString(value);
  const hrefTemplate = options?.href ?? '';
  const external = options?.external ?? false;

  // Resolve :fieldName placeholders in the href template
  const href = hrefTemplate.replaceAll(/:(\w+)/g, (_match, fieldName: string) => {
    const fieldValue = row?.[fieldName];
    return safeString(fieldValue);
  });

  return { type: 'link', text, href, external };
}

function renderIcon(
  value: unknown,
  options?: IconColumnOptions,
): CellRenderResult {
  const key = safeString(value);
  const iconMap = options?.iconMap ?? {};
  const icon = iconMap[key] ?? '';

  return { type: 'icon', text: '', icon };
}

function renderCustom(
  value: unknown,
  options?: CustomColumnOptions,
): CellRenderResult {
  return {
    type: 'custom',
    text: safeString(value),
    rendererKey: options?.renderer,
  };
}
